import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { deepEqual } from 'fast-equals';
import type { Observable } from 'rxjs';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { CurrentUserSelectors } from '@core/account-data';
import type { CurrentUser } from '@core/account-types';
import { filterBoolean } from '@core/common-legacy';
import { EmailProvidersSelectors, SharedEmailProvidersSelectors } from '@core/email-providers-data';
import type { SharedEmailProvider } from '@core/email-providers-types';
import type { EmailProvider } from '@core/email-templates-types';
import type {
  MailboxFormMessageData,
  MailboxMessage,
  MailboxNewMessageDataContext,
  MailboxThread,
  SharedEmailProviderItem,
  SharedEmailProviderItemValue,
} from '@core/mailbox-types';
import type {
  MailboxSelectPersonalProvider,
  MailboxSelectSharedProvider,
} from '@recruitee/mailbox-ui';
import { MailboxSelectType } from '@recruitee/mailbox-ui';

@Injectable({ providedIn: 'root' })
export class EmailProvidersService {
  private store = inject(Store);

  public getConnectedSharedEmailProviderItems(): Observable<
    Array<{ label: string; value: SharedEmailProviderItemValue }>
  > {
    const connectedSharedEmailProviders$ = this.store
      .select(SharedEmailProvidersSelectors.entities({ scope: 'visible' }))
      .pipe(filterBoolean);
    const currentUser$ = this.store.select(CurrentUserSelectors.data).pipe(filterBoolean);
    const currentUserEmailProvider$ = this.store
      .select(EmailProvidersSelectors.data)
      .pipe(filterBoolean);

    return combineLatest([
      currentUserEmailProvider$,
      connectedSharedEmailProviders$,
      currentUser$,
    ]).pipe(
      distinctUntilChanged(deepEqual),
      map(([personalProvider, sharedProviders, currentUser]) => [
        this.getPersonalProviderItem(personalProvider, currentUser),
        ...sharedProviders.map(sharedProvider => this.getSharedProviderItem(sharedProvider)),
      ]),
    );
  }

  public getDefaultEmailProviderItem(
    emailProviderItems: SharedEmailProviderItem[],
    currentEmailProviderId: number | null,
    thread?: MailboxThread,
  ): SharedEmailProviderItemValue {
    const recentSharedEmailProviderItem = currentEmailProviderId
      ? emailProviderItems.find(p => p.value.id === currentEmailProviderId)
      : thread
        ? {
            value: this.findEmailProviderItemById(
              this.findSenderInThread(thread)?.emailProviderId!,
              emailProviderItems,
            ),
          }
        : emailProviderItems[0];

    return (recentSharedEmailProviderItem?.value || emailProviderItems[0]?.value)!;
  }

  public getEmailProviderFromMessage(
    messageWithMetaState: {
      message: Partial<MailboxFormMessageData> | null | undefined;
      context: MailboxNewMessageDataContext | null;
    },
    sharedEmailProviderItems: SharedEmailProviderItem[],
    isMessageFromCache: boolean = false,
  ): SharedEmailProviderItemValue | null {
    const { message, context } = messageWithMetaState;
    const senderFromCache = message?.emailProviderId || null;
    const senderFromThread = context?.thread ? this.findSenderInThread(context?.thread) : null;
    const senderFromMessage = context?.message ? this.findSenderInMessage(context?.message) : null;

    if (isMessageFromCache && senderFromCache) {
      return this.findEmailProviderItemById(senderFromCache, sharedEmailProviderItems);
    }

    if (
      (!senderFromThread && !senderFromMessage) ||
      senderFromThread?.adminId ||
      senderFromMessage?.adminId
    ) {
      return null;
    }
    const emailProviderId = senderFromThread?.emailProviderId || senderFromMessage?.emailProviderId;

    if (emailProviderId) {
      return this.findEmailProviderItemById(emailProviderId!, sharedEmailProviderItems);
    }

    return sharedEmailProviderItems[0]?.value ?? null;
  }

  public findEmailProviderItemById(id: number, items: SharedEmailProviderItem[]) {
    const result = items.find(({ value }) => value.provider.id === id);
    return result ? result.value : null;
  }

  private findSenderInThread(thread: MailboxThread) {
    return (
      thread.messages.reduce(
        (acc, message) =>
          acc
            ? acc
            : [...message.from, ...message.to].find(
                ({ emailProviderId }) => emailProviderId !== null,
              ),
        null,
      ) ?? null
    );
  }

  private findSenderInMessage(message: MailboxMessage) {
    const { from, to } = message;

    if (from[0]?.emailProviderId) {
      return from[0];
    } else if (to[0]?.emailProviderId) {
      return to[0];
    }

    return null;
  }

  private getPersonalProviderItem(personalProvider: EmailProvider, currentUser: CurrentUser): any {
    return {
      label: personalProvider.email,
      value: <MailboxSelectPersonalProvider>{
        type: MailboxSelectType.Personal,
        id: personalProvider.id,
        provider: {
          ...personalProvider,
          name: `${currentUser.firstName} ${currentUser.lastName}`,
          shared: false,
          references: [currentUser],
        },
      },
    };
  }

  private getSharedProviderItem(sharedProvider: SharedEmailProvider): any {
    return {
      label: sharedProvider.email,
      value: <MailboxSelectSharedProvider>{
        type: MailboxSelectType.Shared,
        id: sharedProvider.id,
        provider: {
          ...sharedProvider,
          shared: true,
        },
      },
    };
  }
}
